import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  TemplateRef,
  ViewChild,
  OnInit,
  OnDestroy,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { FieldType, FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';

import { AbstractControl, ReactiveFormsModule, TouchedChangeEvent } from '@angular/forms';
import { MatStepperModule, MatStepper } from '@angular/material/stepper';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom, of, type Observable } from 'rxjs';
import { filter, finalize, take, takeUntil } from 'rxjs/operators';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { WizardDraftService } from '../../services/wizard-draft.service';
import { WizardFooterSlot } from './wizard-footer-slot.service';
import { WizardReviewComponent } from './wizard-review.component';
import { DetailsErrorDisplayComponent } from '../details-error-display.component';
import { FormValidationService } from '../../services/form-validation.service';
import type { EnhancedFormError } from '../form-validation/form-validation.types';
import { WizardStepperComponent } from './wizard-stepper.component';
import { LinkButtonComponent } from '../link-button.component';
import { FormService } from '../../../features/admin/modules/features/form-builder/form.service';
import { InterfaceActionRunnerService } from '../../../features/admin/modules/features/interface-actions/interface-action-runner.service';
import { InterfaceActionsService } from '../../../features/admin/modules/features/interface-actions/interface-actions.service';
import { ModulesStore } from '../../../features/admin/modules/modules.store';
import { UtilityService } from '../../services/utility.service';
import { debugLog } from '../../utils/debug-logger';
import { isEmptyValue } from '../../utils/empty-value.util';
import { anchorEntryOf } from './anchor-entry.util';
import type { Entry, Module } from '../../../features/admin/modules/modules.model';
import type { InterfaceAction } from '../../../features/admin/modules/features/interface-actions/interface-actions.model';
import {
  hostsDocumentsChecklist,
  isVisibleWizardPage,
  pageKeyOf,
  pageTarget,
} from '../review-flag/review-flags.util';
import { WizardHostContext } from './wizard-host-context.service';
import { WizardStepStatusEnum } from './wizard.types';
import type {
  CompletionAction,
  CompletionActionCondition,
  WizardStepperItem,
  WizardStepStatus,
} from './wizard.types';

@Component({
  selector: 'formly-field-wizard',
  imports: [
    FormlyModule,
    ReactiveFormsModule,
    MatStepperModule,
    MatButtonModule,
    MatIconModule,
    WizardReviewComponent,
    WizardStepperComponent,
    LinkButtonComponent,
    DetailsErrorDisplayComponent,
  ],
  template: `
    <div
      class="w-full"
      [class.px-4]="!isCreateFormStyle"
      [class.sm:px-6]="!isCreateFormStyle"
      [class.wizard-style-popup]="props.wizardStyle === 'popup'"
      [class.wizard-style-create-form]="isCreateFormStyle"
      [class.wizard-style-framed]="isFramedStyle">
      <!-- Mobile progress indicator (create-form style shows the step badge in the modal header instead) -->
      @if (!(isCreateFormStyle && hasFooterSlot)) {
        <div class="sm:hidden mb-6">
          <div class="text-center mb-2">Stap {{ currentStepIndex + 1 }} van {{ totalStepCount }}</div>
          <app-wizard-stepper
            [steps]="stepperSteps"
            [currentIndex]="currentStepIndex"
            [statuses]="stepperStatuses"
            [notices]="stepperNotices"
            [compact]="true"
            [interactive]="false" />
        </div>
      }

      <!--
        Desktop stepper — alleen gemount op ≥640px (Tailwind sm).
        CSS-hidden zou beide layouts in DOM laten staan; Angular instantieert dan
        elk '<formly-field>' twee keer (desktop + mobile) wat dubbele FieldType-
        component-instances per veld geeft → twee aparte validators op dezelfde
        FormControl met afwijkende signal-state → INVALID/VALID race. @if om dat te
        voorkomen.
      -->
      @if (isDesktop()) {
      <div class="hidden sm:block">
        <!--
          Framed page layout (Figma "Form Container"): the stepper sits OUTSIDE the white card,
          both centered in a 32px-gap column, and the step content lives in a fixed 668px card.
          Only for page-hosted wizards; the create-form modal and popup styles keep their own chrome.
        -->
        <div [class]="framedContainerClass">
        <!--
          Custom stepper header (Figma house style). Suppressed for the create-form modal style:
          that host renders its own compact "Stap X van Y" badge in the modal header and is
          deliberately chrome-less, so a labeled stepper row here would duplicate it.
        -->
        @if (!isCreateFormStyle) {
          <app-wizard-stepper
            [class]="framedStepperClass"
            [steps]="stepperSteps"
            [currentIndex]="currentStepIndex"
            [reachable]="stepperReachable"
            [statuses]="stepperStatuses"
            [notices]="stepperNotices"
            (stepClick)="goToStep($event)" />
        }
        <div [class]="framedCardClass">
        <mat-stepper [linear]="isLinear && !isNonLinear()" #stepper (selectionChange)="onStepChange($event)">
          @for (step of visibleSteps; track $index; let index = $index; let last = $last) {
            <mat-step
              [stepControl]="step.formControl"
              [label]="step.props?.label"
              [completed]="isStepCompleted(index, step)">
              <ng-template matStepLabel>{{ step.props?.label }}</ng-template>

              <div [class]="framedContentClass">
                <!--
                  Step title inside the card. The Material stepper header (which carried the step
                  label) is now hidden and the custom <app-wizard-stepper> above the stepper shows
                  only compact labels, so the full step title is restored here per Figma (20px Inter
                  medium, ~24px below). Not for the create-form modal style — that host renders the
                  step label in its own header. The review step has its own heading.
                -->
                @if (!isCreateFormStyle) {
                  <h2
                    class="text-[20px] font-medium leading-tight"
                    [class.mb-6]="!isFramedStyle"
                    [class.mb-8]="isFramedStyle">
                    {{ step.props?.label }}
                  </h2>
                  <!-- Host-published, rendered verbatim: the wizard adds no lead-in of its own. -->
                  @if (stepNotice(index); as notice) {
                    <p class="wizard-flag-notice" data-cy="wizard-step-flag-notice">
                      <mat-icon class="icon-size-4" svgIcon="heroicons_solid:flag"></mat-icon>
                      <span>{{ notice }}</span>
                    </p>
                  }
                }
                <form
                  class="w-full flex-1"
                  [class.mb-4]="!isCreateFormStyle && !isFramedStyle"
                  [formGroup]="step.formControl?.parent || step.parent?.formControl || form">
                  <formly-field [field]="step"></formly-field>
                </form>

                @if (!hasFooterSlot) {
                  <div
                    class="w-full flex-1 flex justify-between"
                    [class.mt-4]="!isFramedStyle"
                    [class.mt-12]="isFramedStyle">
                    @if (index > 0) {
                      <!--
                        Figma button group: the back action is a tertiary underlined link with an
                        arrow-left, not a bordered icon button — same treatment as the review step.
                        Non-framed wizards keep the compact icon button they already had.
                      -->
                      @if (isFramedStyle) {
                        <button dd-link-button type="button" icon="heroicons_outline:arrow-left" matStepperPrevious>
                          Terug
                        </button>
                      } @else {
                        <button mat-stroked-button matStepperPrevious>
                          <mat-icon class="icon-size-5 mr-2" svgIcon="mat_solid:chevron_left"></mat-icon>
                        </button>
                      }
                    }

                    @if ((!last || reviewEnabled) && !isNextButtonHidden(step)) {
                      @if (stepHasAction(step)) {
                        <!--
                          Action-carrying step: the Next button runs+awaits an interface action
                          (create entry in the target module) before advancing, so it must NOT use
                          matStepperNext (which advances synchronously). nextStep() owns the advance.
                        -->
                        <button
                          type="button"
                          mat-flat-button
                          class="dd-action-button"
                          [class.ml-auto]="index === 0"
                          [disabled]="isAdvancing() || (!isNonLinear() && step.formControl.status === 'INVALID')"
                          (click)="nextStep()">
                          Volgende stap
                        </button>
                      } @else {
                        <button
                          type="button"
                          mat-flat-button
                          class="dd-action-button"
                          [class.ml-auto]="index === 0"
                          [disabled]="!isNonLinear() && step.formControl.status === 'INVALID'"
                          matStepperNext>
                          Volgende stap
                        </button>
                      }
                    }

                    <!--
                      Runner-only finish button on the last step. In normal module forms the
                      wizard is submitted by the surrounding form host (DetailsService save button),
                      so this only renders when a runner completion handler is present.
                    -->
                    @if (last && !reviewEnabled && isRunnerContext()) {
                      @if (visibleCompletionActions().length) {
                        <div class="ml-auto flex gap-2">
                          @for (ca of visibleCompletionActions(); track ca.interfaceActionId) {
                            <button
                              type="button"
                              mat-flat-button
                              class="dd-action-button"
                              [disabled]="isAdvancing() || step.formControl.status === 'INVALID'"
                              (click)="runCompletionAction(ca)">
                              {{ ca.label }}
                            </button>
                          }
                        </div>
                      } @else {
                        <button
                          type="button"
                          mat-flat-button
                          class="dd-action-button ml-auto"
                          [disabled]="isAdvancing() || step.formControl.status === 'INVALID'"
                          (click)="submit()">
                          Voltooien
                        </button>
                      }
                    }
                  </div>
                }
              </div>
            </mat-step>
          }
          @if (reviewEnabled) {
            <mat-step [completed]="false" label="Controleren">
              <ng-template matStepLabel>Controleren</ng-template>
              <div [class]="framedContentClass">
                <app-wizard-review
                  [steps]="visibleSteps"
                  [revision]="reviewRevision()"
                  [confirmText]="reviewConfirmText"
                  [confirmed]="reviewConfirmed()"
                  (confirmedChange)="onReviewConfirmedChange($event)"
                  (editStep)="goToStep($event)" />

                <!--
                  Non-linear submit was blocked by missing required fields. The jump lands on the
                  first offending step, so without this list the user has no idea what else is wrong.
                  Clicking an entry goes to that step and focuses the field.
                -->
                @if (submitErrors().length) {
                  <app-details-error-display
                    class="mt-4 block"
                    [errors]="submitErrors()"
                    [showRevalidateHint]="false"
                    (errorClick)="goToErrorField($event)" />
                }

                <!-- Non-linear: submit was blocked purely because the declaration is unticked. -->
                @if (confirmMissing()) {
                  <p class="mt-2 text-sm text-warn">Bevestig de verklaring hierboven om de onboarding in te dienen.</p>
                }

                @if (!hasFooterSlot) {
                  <div class="w-full flex-1 flex justify-between items-center mt-6">
                    <button dd-link-button type="button" icon="heroicons_outline:arrow-left" matStepperPrevious>
                      Terug
                    </button>

                    @if (isRunnerContext()) {
                      @if (visibleCompletionActions().length) {
                        <div class="ml-auto flex gap-2">
                          @for (ca of visibleCompletionActions(); track ca.interfaceActionId) {
                            <button
                              type="button"
                              mat-flat-button
                              class="dd-action-button"
                              [disabled]="submitDisabled()"
                              (click)="runCompletionAction(ca)">
                              {{ ca.label }}
                            </button>
                          }
                        </div>
                      } @else {
                        <button
                          type="button"
                          mat-flat-button
                          class="dd-action-button ml-auto"
                          [disabled]="submitDisabled()"
                          (click)="submit()">
                          Voltooien
                        </button>
                      }
                    } @else if (showExitToStatus()) {
                      <!--
                        Reworking a denied request with nothing changed yet: resubmitting would hand
                        the reviewer back an identical request, so the only offer is a way out.
                      -->
                      <button
                        type="button"
                        mat-flat-button
                        class="dd-secondary-button ml-auto"
                        data-cy="wizard-exit-to-status"
                        (click)="exitToStatus()">
                        Terug naar status
                      </button>
                    } @else if (visibleCompletionActions().length) {
                      <!--
                        Page-hosted wizard (no create-form runner): render the configured completion
                        actions on the review step. buttonStyle 'outline' = secondary
                        (dd-secondary-button: filled neutral, Figma 2325-104850 "Send to user"),
                        anders primair (dd-action-button). Secundair links, primair rechts (Figma).
                      -->
                      <div class="ml-auto flex gap-2">
                        @for (ca of sortedPageCompletionActions(); track ca.interfaceActionId) {
                          @if (ca.buttonStyle === 'outline') {
                            <button
                              type="button"
                              mat-flat-button
                              class="dd-secondary-button"
                              [disabled]="submitDisabled()"
                              (click)="runPageCompletionAction(ca)">
                              {{ ca.label }}
                            </button>
                          } @else {
                            <button
                              type="button"
                              mat-flat-button
                              class="dd-action-button"
                              [disabled]="submitDisabled()"
                              (click)="runPageCompletionAction(ca)">
                              {{ ca.label }}
                            </button>
                          }
                        }
                      </div>
                    }
                  </div>
                }
              </div>
            </mat-step>
          }
        </mat-stepper>
        </div>
        </div>
      </div>
      }

      <!-- Mobile step content — alleen gemount op <640px, zie comment hierboven. -->
      @if (!isDesktop()) {
      <div class="sm:hidden flex flex-col w-full max-w-xl mx-auto">
        <div class="text-xl font-medium mb-4">
          {{ isReviewActive() ? 'Controleren' : visibleSteps[currentStepIndex]?.props?.label }}
        </div>

        <!-- The review step is not a form page and can carry no notice of its own. -->
        @if (!isReviewActive() && stepNotice(currentStepIndex); as notice) {
          <p class="wizard-flag-notice" data-cy="wizard-step-flag-notice">
            <mat-icon class="icon-size-4" svgIcon="heroicons_solid:flag"></mat-icon>
            <span>{{ notice }}</span>
          </p>
        }

        @if (isReviewActive()) {
          <div class="w-full flex-1 mb-6">
            <app-wizard-review
              [steps]="visibleSteps"
              [revision]="reviewRevision()"
              [heading]="null"
              [confirmText]="reviewConfirmText"
              [confirmed]="reviewConfirmed()"
              (confirmedChange)="reviewConfirmed.set($event)"
              (editStep)="goToStep($event)" />
          </div>
        } @else {
          <form
            class="w-full flex-1 mb-6"
            [formGroup]="getCurrentStep().formControl?.parent || getCurrentStep().parent?.formControl || form">
            <formly-field [field]="getCurrentStep()"></formly-field>
          </form>
        }

        @if (!hasFooterSlot) {
          <div class="flex justify-between gap-4">
            @if (currentStepIndex > 0) {
              <button type="button" mat-stroked-button (click)="previousStep()" class="flex-1">
                <mat-icon class="icon-size-5 mr-2" svgIcon="mat_solid:chevron_left"></mat-icon>
                Vorige
              </button>
            }

            @if (!isLastStep() && !isNextButtonHidden(getCurrentStep())) {
              <button
                type="button"
                mat-flat-button
                [disabled]="isAdvancing() || getCurrentStep().formControl.status === 'INVALID'"
                (click)="nextStep()"
                class="flex-1 dd-action-button">
                Volgende
              </button>
            }

            @if (isLastStep() && isRunnerContext()) {
              @if (visibleCompletionActions().length) {
                @for (ca of visibleCompletionActions(); track ca.interfaceActionId) {
                  <button
                    type="button"
                    mat-flat-button
                    [disabled]="submitDisabled()"
                    (click)="runCompletionAction(ca)"
                    class="flex-1 dd-action-button">
                    {{ ca.label }}
                  </button>
                }
              } @else {
                <button
                  type="button"
                  mat-flat-button
                  [disabled]="submitDisabled()"
                  (click)="submit()"
                  class="flex-1 dd-action-button">
                  Voltooien
                </button>
              }
            }

            @if (isLastStep() && !isRunnerContext() && showExitToStatus()) {
              <button
                type="button"
                mat-flat-button
                class="flex-1 dd-secondary-button"
                data-cy="wizard-exit-to-status"
                (click)="exitToStatus()">
                Terug naar status
              </button>
            }

            @if (isLastStep() && !isRunnerContext() && !showExitToStatus() && visibleCompletionActions().length) {
              @for (ca of sortedPageCompletionActions(); track ca.interfaceActionId) {
                @if (ca.buttonStyle === 'outline') {
                  <button
                    type="button"
                    mat-flat-button
                    [disabled]="submitDisabled()"
                    (click)="runPageCompletionAction(ca)"
                    class="flex-1 dd-secondary-button">
                    {{ ca.label }}
                  </button>
                } @else {
                  <button
                    type="button"
                    mat-flat-button
                    [disabled]="submitDisabled()"
                    (click)="runPageCompletionAction(ca)"
                    class="flex-1 dd-action-button">
                    {{ ca.label }}
                  </button>
                }
              }
            }
          </div>
        }
      </div>
      }

      <ng-template #runnerFooterTpl>
        <div class="flex justify-between items-center gap-4">
          @if (currentStepIndex > 0) {
            <button type="button" mat-stroked-button (click)="previousStep()">
              <mat-icon class="icon-size-5" svgIcon="mat_solid:chevron_left"></mat-icon>
            </button>
          } @else {
            <span></span>
          }
          <div class="flex gap-3">
            @if (!isLastStep() && !isNextButtonHidden(getCurrentStep())) {
              <button
                type="button"
                mat-flat-button
                class="dd-action-button"
                [disabled]="isAdvancing() || getCurrentStep().formControl.status === 'INVALID'"
                (click)="nextStep()">
                Volgende stap
              </button>
            }
            @if (isLastStep() && isRunnerContext()) {
              @if (visibleCompletionActions().length) {
                <!-- Equal-width completion buttons: auto-cols-fr sizes every column to the widest. -->
                <div class="grid grid-flow-col auto-cols-fr gap-3">
                  @for (ca of sortedVisibleCompletionActions(); track ca.interfaceActionId) {
                    <!-- Figma: the "add another" (reset) action is the secondary button, close is primary. -->
                    @if (ca.behavior === 'reset') {
                      <button
                        type="button"
                        mat-stroked-button
                        class="w-full wizard-footer-action"
                        [disabled]="submitDisabled()"
                        (click)="runCompletionAction(ca)">
                        {{ ca.label }}
                      </button>
                    } @else {
                      <button
                        type="button"
                        mat-flat-button
                        class="dd-action-button w-full wizard-footer-action"
                        [disabled]="submitDisabled()"
                        (click)="runCompletionAction(ca)">
                        {{ ca.label }}
                      </button>
                    }
                  }
                </div>
              } @else {
                <button
                  type="button"
                  mat-flat-button
                  class="dd-action-button"
                  [disabled]="submitDisabled()"
                  (click)="submit()">
                  Voltooien
                </button>
              }
            }
          </div>
        </div>
      </ng-template>
    </div>
  `,
  styles: [
    `
    /* The custom <app-wizard-stepper> header is now the house style everywhere, so Material's
       own horizontal stepper header is always hidden (the per-style blocks below are kept for
       clarity but are redundant with this global rule). */
    :host ::ng-deep .mat-horizontal-stepper-header-container {
      display: none;
    }

    /*
      Framed page layout (Figma "Form content"): the 668px card owns the inset via px-40 pt-36
      pb-40. Material's horizontal content container adds its own 0 24px 24px on top of that,
      which narrowed the content to 540px and left an extra 24px under the button row — so it is
      zeroed here and the card's padding is the only inset.
    */
    :host ::ng-deep .wizard-style-framed {
      .mat-horizontal-content-container {
        padding: 0;
      }

      /* Section-heading typography (Figma Title/Small) lives in the global stylesheet — the
         encapsulated rule lost to Tailwind's .font-bold utility. See .wizard-style-framed h3. */

      /* Figma "Section": 32px above a heading (Form gap), 12px down to its first field. */
      formly-field-title {
        display: block;
        margin-top: 32px;
        margin-bottom: 12px;
      }
    }

    :host ::ng-deep .wizard-style-popup {
      .mat-horizontal-stepper-header-container {
        display: none;
      }

      form {
        max-width: 28rem;
      }

      /* Figma finish: card radius/padding via --dd-field-* tokens, see task-6 scaffold. */
    }

    /* Create-form (Aanmaakformulier) style: the hosting modal renders header (step badge) and
       footer, so the wizard is chrome-less: no stepper header, no content padding. */
    :host ::ng-deep .wizard-style-create-form {
      .mat-horizontal-stepper-header-container {
        display: none;
      }

      .mat-horizontal-content-container {
        padding: 0;
      }

      /* Figma: field labels and section headings are neutral text, not the theme accent color. */
      label,
      mat-label,
      h1,
      h2,
      h3,
      h4 {
        color: var(--fuse-text-default);
      }

      /* Figma Body/Large: intro/description text fields render at 15px. */
      formly-field-text .text-md {
        font-size: 15px;
        line-height: 1.5;
      }
    }

    /* One-line "the reviewer asked you to change this" notice under a step title. Pulled up into
       the heading's bottom margin so it reads as part of the title block rather than as the first
       thing on the form. Colour via the shared warning token, so dark mode follows along. */
    .wizard-flag-notice {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin: -0.5rem 0 1.25rem;
      font-size: 0.875rem;
      color: var(--dd-badge-warning-fg);
    }

    .wizard-flag-notice strong {
      font-weight: 600;
    }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyWizardComponent
  extends FieldType
  implements OnInit, OnDestroy, AfterViewChecked, AfterViewInit
{
  @ViewChild('stepper') stepper!: MatStepper;
  @ViewChild('runnerFooterTpl') runnerFooterTpl!: TemplateRef<unknown>;

  readonly #draftService = inject(WizardDraftService);
  readonly #formService = inject(FormService);
  readonly #elementRef = inject(ElementRef);
  readonly #cdr = inject(ChangeDetectorRef);
  readonly #destroy$ = injectDestroy();
  readonly #breakpointObserver = inject(BreakpointObserver);
  readonly #runner = inject(InterfaceActionRunnerService);
  readonly #interfaceActionsService = inject(InterfaceActionsService);
  readonly #modulesStore = inject(ModulesStore);
  readonly #utilityService = inject(UtilityService);
  readonly #formValidationService = inject(FormValidationService);
  readonly #router = inject(Router);
  readonly #activatedRoute = inject(ActivatedRoute);
  /**
   * Optional projection slot into the hosting CreateFormModalComponent's footer. Only provided
   * there (via its own injector) — in normal module forms / entry pages this stays `null` and the
   * wizard keeps rendering its buttons inline, unchanged.
   */
  readonly #footerSlot = inject(WizardFooterSlot, { optional: true });
  /** The host's half of the host ↔ wizard channel — see {@link WizardHostContext}. `null` for a wizard used in a normal module form or entry page. */
  readonly #hostContext = inject(WizardHostContext, { optional: true });

  /**
   * Reactive desktop-vs-mobile flag — matcht Tailwind's `sm` breakpoint (≥640px).
   * Initial value uit `matchMedia` zodat de eerste render direct correct mount;
   * daarna live-updated via BreakpointObserver bij resize.
   */
  readonly isDesktop = signal<boolean>(
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 640px)').matches : true
  );

  /**
   * True while an action-carrying step is running+awaiting its interface action. Blocks the
   * Next/Voltooien buttons so the user cannot double-submit or advance before the create resolves.
   */
  readonly isAdvancing = signal(false);

  /** Bumped on every form-value change so completion-button conditions re-evaluate reactively. */
  readonly #modelVersion = signal(0);

  /**
   * Bumped whenever the active step changes.
   *
   * The review summary cannot rely on {@link #modelVersion} alone. That signal is driven purely by
   * `formControl.valueChanges`, and a dozen field types write with `{ emitEvent: false }` — the
   * autocomplete's free-text commit, address, geolocation, inline-entries. Those writes never bump
   * it, so a value entered through one of them would be missing from the summary the user then
   * confirms. Folding the step in means arriving at the review step always rebuilds it, which is
   * the only moment the summary is on screen anyway.
   */
  readonly #stepRevision = signal(0);

  /** Record a step change so anything keyed on {@link reviewRevision} recomputes. */
  #bumpStepRevision(): void {
    this.#stepRevision.update(v => v + 1);
    this.#visitedSteps.add(this.#stepIdentity(this.currentStepIndex));
  }

  /**
   * Stable identity of a step, for {@link #visitedSteps}. Deliberately NOT the index: conditional
   * steps drop out of {@link visibleSteps} as the model changes, and every step after the vanished
   * one shifts down a slot. Keyed on the index, a step the user has never opened would inherit its
   * neighbour's "visited" flag and start showing the incomplete badge.
   */
  #stepIdentity(index: number): string {
    const step = this.visibleSteps[index];
    if (!step) {
      // The virtual review step sits one past the last form step and has no config of its own.
      return 'review';
    }
    return String(step.key ?? step.id ?? `index:${index}`);
  }

  /**
   * Steps the user has actually opened. This is the ONLY honest signal that someone has engaged
   * with a step: control state is not, because several field types write their own value while
   * initialising — the required-documents checklist rewrites its summary object as soon as the
   * document templates arrive — and a write through a ControlValueAccessor marks the control dirty
   * without a human touching anything. Deriving "the user left this half done" from `dirty` put a
   * dashed badge on every such step the moment ANY field on the form changed.
   *
   * Not persisted in the draft on purpose: after a reload nobody is mid-step, so every step falls
   * back to its data-only verdict until it is opened again.
   *
   * Keyed on {@link #stepIdentity}, not on the index — see there.
   */
  readonly #visitedSteps = new Set<string>();

  /**
   * Set once a submit has been blocked. From that moment every unfinished step may show its
   * incomplete badge, visited or not — that is the one moment the user is being told what is
   * missing everywhere at once.
   */
  #submitBlocked = false;

  /**
   * Bumped whenever controls are touched programmatically (a blocked non-linear submit). Touching
   * a control does not emit valueChanges, so without this the stepper error markers would not
   * refresh after the user is sent back to the first invalid step.
   */
  readonly #touchedRevision = signal(0);

  /**
   * Created entries from action-carrying steps, keyed by the step's resultKey (or step key).
   * Forwarded to the runner's interface actions so later steps can read earlier creates via
   * basedOn:'stepResult'. Plain object (not a signal) — read only inside async handlers.
   */
  #stepResults: Record<string, Entry> = {};

  isLinear = true; // Steps must be completed sequentially
  currentStepIndex = 0;

  #draftLoaded = false;
  /** Pending deferred start-step jump ({@link #applyStartStep}), cancelled on destroy. */
  #startStepTimer: ReturnType<typeof setTimeout> | null = null;
  #moduleId: string | null = null;
  /**
   * Entry this wizard edits, captured once at init. Drafts are keyed by it so a draft of one entry
   * is never restored over another entry of the same module (e.g. every onboarding runs on the same
   * page module). Stays undefined while creating, and is deliberately NOT refreshed after a save so
   * save/markAsSaved keep addressing the key the draft was written under.
   */
  #draftEntryId: string | undefined;

  /** Whether a footer slot was injected — when true, buttons render only via the projected template. */
  protected get hasFooterSlot(): boolean {
    return !!this.#footerSlot;
  }

  /** Whether this wizard uses the create-form (Aanmaakformulier) style from the form builder. */
  protected get isCreateFormStyle(): boolean {
    return this.props?.wizardStyle === 'create-form';
  }

  /**
   * Framed page layout (Figma "Form Container"): stepper OUTSIDE a fixed 668px white card, both
   * centered. Applies to page-hosted wizards (onboarding) — not the create-form modal (chrome-less,
   * hasFooterSlot) and not the popup style, which keep their own framing.
   */
  protected get isFramedStyle(): boolean {
    return !this.isCreateFormStyle && !this.hasFooterSlot && this.props?.wizardStyle !== 'popup';
  }

  /** Column wrapper: centers stepper + card with a 32px gap (Figma) when framed. */
  protected get framedContainerClass(): string {
    return this.isFramedStyle ? 'flex flex-col items-center gap-8' : '';
  }

  /** Stepper row: full-width centered above the card when framed; else keeps its 32px bottom gap. */
  protected get framedStepperClass(): string {
    return this.isFramedStyle ? 'block w-full' : 'block mb-8';
  }

  /** The white card (Figma Form content: 668px, rounded-16, px-40 pt-36 pb-40) when framed. */
  protected get framedCardClass(): string {
    return this.isFramedStyle ? 'bg-card rounded-2xl w-[668px] max-w-full px-10 pt-9 pb-10' : '';
  }

  /** Per-step content: full-width inside the card when framed; else the legacy centered max-w-xl. */
  protected get framedContentClass(): string {
    return this.isFramedStyle ? 'flex flex-col w-full' : 'flex flex-col max-w-xl m-auto';
  }

  /**
   * Non-linear mode: every step is freely navigable, "Volgende stap" never blocks, and validation
   * runs once on the review step instead of per step. Driven by the configured
   * `nonLinearCondition`, evaluated against the form model and, failing that, the hosting entry —
   * manual onboarding reads it from `entry.user.entry.meta-data.onboarding-choice`.
   *
   * Recomputed on every form-value change (#modelVersion) so a condition pointing at a form field
   * switches modes live. Without a configured condition the wizard stays linear, unchanged.
   */
  readonly isNonLinear = computed<boolean>(() => {
    this.#modelVersion();
    const condition = this.field.props?.['nonLinearCondition'] as CompletionActionCondition | undefined;
    if (!condition?.fieldKeyPath) {
      return false;
    }
    return this.#conditionMatches(condition);
  });

  /**
   * Resolve a condition key path against the live form model first and the hosting entry second.
   * The model lookup mirrors #matchesCondition (wrap the raw value in `{ entry }`); the entry
   * fallback lets a condition address data outside this form, such as the invite choice stored on
   * the linked user.
   *
   * A host can close that entry fallback with `formState.nonLinearFromEntry: false` — the onboarding
   * page does so when the visitor is filling in their OWN onboarding, where the invite choice on the
   * linked user says nothing about who is at the keyboard. The model lookup is never gated, so a
   * condition pointing at a form field behaves the same either way.
   */
  #conditionMatches(condition: CompletionActionCondition): boolean {
    const model = this.form?.getRawValue() ?? {};
    const fromModel = UtilityService.keyPathToValue({ entry: model, id: undefined } as Entry, condition.fieldKeyPath);
    // `formState.entry` is the form model merged onto the persisted entry; `formState.hostEntry` is
    // the persisted entry as the page resolved it. The latter is the only one that carries linked
    // data (e.g. the embedded user) on pages that fetch their entry outside the ModulesStore.
    const entryFallbackAllowed = this.options?.formState?.['nonLinearFromEntry'] !== false;
    const candidates = (
      entryFallbackAllowed ? [this.options?.formState?.entry, this.options?.formState?.hostEntry] : []
    ).filter(Boolean) as Entry[];
    let actual = fromModel;
    for (const entry of candidates) {
      if (actual !== undefined && actual !== null) {
        break;
      }
      actual = UtilityService.keyPathToValue(entry, condition.fieldKeyPath);
    }
    if (actual !== null && typeof actual === 'object') {
      const obj = actual as Record<string, unknown>;
      return obj['value'] === condition.value || obj['id'] === condition.value || obj['slug'] === condition.value;
    }
    return actual === condition.value;
  }

  /** Whether the generic review (summary) step is enabled via the form builder toggle. */
  protected get reviewEnabled(): boolean {
    return this.field.props?.['enableReviewStep'] === true;
  }

  /** Step count including the virtual review step. */
  protected get totalStepCount(): number {
    return this.visibleSteps.length + (this.reviewEnabled ? 1 : 0);
  }

  /** True while the virtual review step is active (index directly after the last form step). */
  protected isReviewActive(): boolean {
    return this.reviewEnabled && this.currentStepIndex === this.visibleSteps.length;
  }

  /** Recompute trigger for the review summary — formly mutates the model in place. */
  protected readonly reviewRevision = computed(() => this.#modelVersion() + this.#stepRevision());

  /** Confirm-checkbox text for the review step; null/empty hides the checkbox. */
  protected get reviewConfirmText(): string | null {
    const text = this.field.props?.['reviewConfirmText'];
    return typeof text === 'string' && text.trim() !== '' ? text : null;
  }

  /** Whether the review confirm-checkbox is checked; gates the completion buttons. */
  readonly reviewConfirmed = signal(false);

  /** Jump to a form step from the review step's Bewerken button. */
  goToStep(index: number): void {
    if (this.stepper) {
      this.stepper.selectedIndex = index;
      return;
    }
    this.currentStepIndex = index;
    this.#bumpStepRevision();
    this.#publishStepInfo();
    this.saveDraft();
  }

  /**
   * Disabled state of Voltooien/completion buttons. On the review step there is no current form
   * step (getCurrentStep() is out of bounds), so the whole form's validity decides.
   */
  submitDisabled(): boolean {
    if (this.isAdvancing()) {
      return true;
    }
    // Non-linear: the completion buttons stay CLICKABLE even when the form is incomplete. Blocking
    // them would leave the user with a dead button and no clue what is wrong; instead the click
    // handler surfaces the errors and jumps to the first offending step (#validateBeforeSubmit).
    if (this.isNonLinear()) {
      return false;
    }
    if (this.isReviewActive()) {
      return this.form.invalid || (this.reviewConfirmText !== null && !this.reviewConfirmed());
    }
    return this.getCurrentStep()?.formControl?.status === 'INVALID';
  }

  /**
   * Non-linear submit gate. Returns true when the wizard may submit; otherwise it surfaces what is
   * missing and returns false:
   *  - form invalid  → touch every control so the errors render, jump to the first invalid step;
   *  - confirm box unticked → point the user at the confirmation.
   * Linear mode never reaches this: there the buttons are disabled until everything is valid.
   */
  #validateBeforeSubmit(): boolean {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.#submitBlocked = true;
      this.#touchedRevision.update(v => v + 1);
      // A snackbar is gone in four seconds and the jump lands the user on ONE offending step, which
      // says nothing about the other three. Keep the full list on screen, the same way the detail
      // page does, so they can work it off and click straight to each field.
      this.submitErrors.set(this.#formValidationService.getEnhancedErrors(this.form, this.field.fieldGroup ?? []));
      const firstInvalid = this.visibleSteps.findIndex(step => step.formControl?.invalid === true);
      if (firstInvalid >= 0) {
        this.goToStep(firstInvalid);
      }
      this.#utilityService.notify('Niet alle verplichte velden zijn ingevuld. Controleer de gemarkeerde stappen.');
      this.#cdr.markForCheck();
      return false;
    }
    this.submitErrors.set([]);
    // The form validated, so the "show every unfinished step" override has done its job. Leaving it
    // on would make any step revealed later read as incomplete for the rest of the session.
    this.#submitBlocked = false;
    if (this.reviewConfirmText !== null && !this.reviewConfirmed()) {
      this.confirmMissing.set(true);
      this.#utilityService.notify('Bevestig eerst de verklaring voordat je de onboarding indient.');
      this.#cdr.markForCheck();
      return false;
    }
    return true;
  }

  /**
   * Required fields still missing at the last submit attempt. Empty until someone submits, and
   * cleared as soon as a submit passes validation.
   */
  readonly submitErrors = signal<EnhancedFormError[]>([]);

  /** Jump to the step holding a field from the error list and focus it. */
  protected goToErrorField(error: EnhancedFormError): void {
    const stepIndex = this.visibleSteps.findIndex(step => this.#stepContainsKey(step, error.key));
    if (stepIndex >= 0) {
      this.goToStep(stepIndex);
    }
    // After the step swap: the target only exists once its step is rendered.
    setTimeout(() => {
      const target = error.fieldId ? document.getElementById(error.fieldId) : null;
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      (target as HTMLInputElement | null)?.focus?.();
    });
  }

  #stepContainsKey(step: FormlyFieldConfig, key: string | undefined): boolean {
    if (!key) {
      return false;
    }
    let found = false;
    const walk = (fields: FormlyFieldConfig[] | undefined) => {
      for (const field of fields ?? []) {
        if (found) {
          return;
        }
        if (String(field.key ?? '') === String(key) || field.props?.['keyPath'] === key) {
          found = true;
          return;
        }
        walk(field.fieldGroup);
      }
    };
    walk(step.fieldGroup);
    return found;
  }

  /** Set when a non-linear submit was blocked purely by the unticked confirm checkbox. */
  readonly confirmMissing = signal(false);

  /**
   * Whether a completion button may fire on an incomplete form. Only the secondary button
   * (buttonStyle 'outline') may — that is the "hand this over unfinished" action. Everything else,
   * including the primary submit, goes through #validateBeforeSubmit first.
   */
  #allowsIncompleteSubmit(ca: CompletionAction): boolean {
    return ca.buttonStyle === 'outline';
  }

  /** Track the confirm checkbox and clear the "tick this first" hint as soon as it is ticked. */
  protected onReviewConfirmedChange(confirmed: boolean): void {
    this.reviewConfirmed.set(confirmed);
    if (confirmed) {
      this.confirmMissing.set(false);
    }
  }

  /**
   * Publish current-step info to the hosting modal (create-form style only) so it can render
   * the "Stap X van Y" badge and step label in its header.
   */
  #publishStepInfo(): void {
    if (!this.#footerSlot || !this.isCreateFormStyle) {
      return;
    }
    const current = this.getCurrentStep();
    const next = {
      index: this.currentStepIndex,
      count: this.totalStepCount,
      label: this.isReviewActive() ? 'Controleren' : ((current?.props?.label as string) ?? ''),
    };
    const prev = this.#footerSlot.stepInfo();
    if (prev?.index !== next.index || prev?.count !== next.count || prev?.label !== next.label) {
      this.#footerSlot.stepInfo.set(next);
    }
  }

  // Get visible steps (filter out conditionally hidden steps)
  /**
   * The steps the user can actually see, memoized like {@link stepperSteps} so the binding keeps a
   * stable array reference across change detection.
   *
   * The reference matters more here than anywhere else: `[steps]` feeds WizardReviewComponent, and
   * because that is a signal input a fresh array every read invalidates its `sections` computed,
   * which walks every step's whole field tree and formats every leaf. The `revision` input exists
   * precisely to control when that rebuild happens, and an unstable reference defeated it — the
   * summary was being rebuilt on every cycle while the user was still typing on step one.
   *
   * Signature: the fieldGroup identity plus the visible indices. The identity check catches a
   * regenerated form (same shape, new objects); the indices catch a step being hidden or revealed.
   */
  #visibleStepsCache: { group: FormlyFieldConfig[] | undefined; sig: string; value: FormlyFieldConfig[] } | null = null;
  get visibleSteps(): FormlyFieldConfig[] {
    const all = this.field.fieldGroup;
    const value: FormlyFieldConfig[] = [];
    const indices: number[] = [];
    (all ?? []).forEach((step, index) => {
      // Same predicate as the onboarding status page's page list (visibleWizardPages): the two
      // index into the same stepper, so they cannot be allowed to disagree on what is in it.
      if (isVisibleWizardPage(step)) {
        value.push(step);
        indices.push(index);
      }
    });
    // Key on identity, not just position: an in-place reorder or a `fieldGroup[i] = newStep`
    // preserves the index list, and a position-only signature would then hand back the previous
    // step object — which getCurrentStep() and #runPendingStepActions() index into.
    const sig = value.map((step, i) => `${indices[i]}:${step.key ?? step.id ?? ''}`).join(',');
    if (this.#visibleStepsCache?.group !== all || this.#visibleStepsCache?.sig !== sig) {
      this.#visibleStepsCache = { group: all, sig, value };
    }
    return this.#visibleStepsCache.value;
  }

  /**
   * Labels feeding the custom stepper header: the visible form steps plus the virtual review step.
   * Memoized on a label signature so the binding keeps a stable array reference across change
   * detection — a fresh array each cycle would trip checkNoChanges on the always-rendered header.
   */
  #stepperStepsCache: { sig: string; value: WizardStepperItem[] } | null = null;
  get stepperSteps(): WizardStepperItem[] {
    const labels = this.visibleSteps.map(step => (step.props?.label as string) ?? '');
    if (this.reviewEnabled) {
      labels.push('Controleren');
    }
    const sig = labels.join('');
    if (this.#stepperStepsCache?.sig !== sig) {
      this.#stepperStepsCache = { sig, value: labels.map(label => ({ label })) };
    }
    return this.#stepperStepsCache.value;
  }

  /**
   * Notice per VISIBLE step (what the line under the step title shows): the host's page notice,
   * plus — for the step hosting the documents checklist, when the host also published a documents
   * notice — that notice appended after `; ` (the page notice is about the step as a whole, the
   * document lines are about individual rows on it).
   *
   * `computed()`, not a plain getter: `#modelVersion` (bumped on every form-value change, same
   * proxy {@link isNonLinear} uses) stands in for `visibleSteps`/`hostsDocumentsChecklist`, which
   * read the plain, non-signal Formly field tree directly — hide expressions and a conditional
   * documents-checklist field both only change in response to a model change. Opus-verified:
   * that is true only because `formly-root-config.ts`'s `FORMLY_ROOT_CONFIG.extras.
   * checkExpressionOn: 'modelChange'` re-evaluates `hideExpression`/`expressionProperties` on model
   * change instead of on every CD tick — this computed's cache-invalidation contract is LOAD-
   * BEARING on that setting; changing it in formly-root-config.ts without revisiting this computed
   * would silently make step notices go stale. Recomputes a fresh array on every model change
   * regardless of content — {@link stepperNotices} below is the one place that turns this into a
   * stable reference for `[notices]`.
   */
  readonly #stepNotices = computed<Array<string | null>>(() => {
    this.#modelVersion();
    const steps = this.visibleSteps;
    const published = this.#hostContext?.stepNotices() ?? {};
    const documentsNotice = this.#hostContext?.documentsNotice() ?? '';
    const pageNotices = steps.map(step => published[pageTarget(pageKeyOf(step))] || null);
    if (!documentsNotice) {
      return pageNotices;
    }
    /*
      The documents notice names no page at all (#21291): it belongs to the step that renders the
      checklist those documents live on — found by what the page contains, using the same helper
      the status page uses to send the user there. Only walked when there is something to place,
      so a wizard without a documents notice never pays for the tree walk.
    */
    return pageNotices.map((pageNotice, index) =>
      hostsDocumentsChecklist(steps[index])
        ? pageNotice
          ? `${pageNotice}; ${documentsNotice}`
          : documentsNotice
        : pageNotice
    );
  });

  /** The last array {@link stepperNotices} returned — its one caching layer, compared by content. */
  #lastStepperNotices: Array<string | null> | null = null;

  /**
   * Notice per stepper step, index-aligned with {@link stepperSteps}: {@link #stepNotices} with —
   * under exactly the {@link reviewEnabled} condition {@link stepperSteps} uses — a trailing null
   * for the virtual review step, which hosts no page and can carry no notice of its own.
   *
   * The single caching layer for `[notices]="stepperNotices"` (`wizard-stepper.component.ts`'s
   * signal input): compares the padded result against the last array THIS getter returned,
   * content-wise (per-index string equality), and keeps handing out that same reference when
   * nothing actually changed — a fresh array on every read would trip checkNoChanges on the
   * always-rendered stepper header.
   */
  get stepperNotices(): Array<string | null> {
    const steps = this.#stepNotices();
    const padded = this.reviewEnabled ? [...steps, null] : steps;
    const previous = this.#lastStepperNotices;
    if (previous && previous.length === padded.length && previous.every((value, index) => value === padded[index])) {
      return previous;
    }
    this.#lastStepperNotices = padded;
    return padded;
  }

  /** One-line notice under a step title, or null when the host published none for this step. */
  stepNotice(index: number): string | null {
    return this.#stepNotices()[index] ?? null;
  }

  /**
   * Per-step reachability for the custom stepper header, memoized like {@link stepperSteps} to keep
   * a stable reference. Mirrors the mat-step `[completed]`/overlay gating: an unreachable step is
   * shown with a not-allowed cursor and cannot be navigated to.
   */
  #stepperReachableCache: { sig: string; value: boolean[] } | null = null;
  get stepperReachable(): boolean[] {
    const count = this.totalStepCount;
    const value: boolean[] = [];
    for (let i = 0; i < count; i++) {
      value.push(this.isStepReachable(i));
    }
    const sig = value.map(reachable => (reachable ? '1' : '0')).join('');
    if (this.#stepperReachableCache?.sig !== sig) {
      this.#stepperReachableCache = { sig, value };
    }
    return this.#stepperReachableCache.value;
  }

  /**
   * Per-step status for the custom stepper header (Figma "Interactive wizard steps"), derived from
   * what the user actually filled in rather than from where they are in the flow:
   *
   * - nothing filled in, or never opened and still missing something → `not-started`
   * - the user has been on this step and something is still missing  → `incomplete`
   * - nothing missing                                                → `complete`
   *
   * `incomplete` requires the user to have OPENED the step (or a submit to have been blocked).
   * Onboarding steps arrive PRE-FILLED — name and e-mail from the invitation, company data from the
   * KvK/RDW api-mapping — and several field types write their own value on init, so neither the
   * presence of data nor `dirty` says anything about the user. A prefilled step that is genuinely
   * finished still earns its check; only the half-finished signal waits for the user to show up.
   *
   * Completeness leans on the control's own validity rather than on "every field has a value", so
   * an untouched OPTIONAL field never keeps a step at `incomplete` forever. The emptiness check
   * covers the reverse: a step whose fields are all optional is valid from the moment the form
   * initialises, and reporting it complete before anything was entered would make the header lie.
   *
   * NON-LINEAR ONLY. An empty array is the signal for the stepper to keep its positional model, so
   * the linear wizard, the mobile indicator and the submitted-onboarding header stay untouched.
   * The design is explicit that grey/locked treatment belongs to the linear flow, not here.
   *
   * Memoized on a signature like {@link stepperReachable} to keep a stable array reference.
   */
  readonly #noStatuses: WizardStepStatus[] = [];
  #stepperStatusesCache: { sig: string; value: WizardStepStatus[] } | null = null;
  get stepperStatuses(): WizardStepStatus[] {
    if (!this.isNonLinear()) {
      return this.#noStatuses;
    }
    this.#modelVersion();
    this.#touchedRevision();
    const count = this.totalStepCount;
    const steps = this.visibleSteps;
    const value: WizardStepStatus[] = [];
    for (let i = 0; i < count; i++) {
      const engaged =
        i === this.currentStepIndex || this.#visitedSteps.has(this.#stepIdentity(i)) || this.#submitBlocked;
      value.push(this.#stepStatus(steps[i], engaged));
    }
    const sig = value.join(',');
    if (this.#stepperStatusesCache?.sig !== sig) {
      this.#stepperStatusesCache = { sig, value };
    }
    return this.#stepperStatusesCache.value;
  }

  /**
   * Status of a single step, aggregated over the controls of ITS OWN leaf fields.
   *
   * It deliberately does NOT read `step.formControl`. Formly hands a field group without its own
   * `key` the PARENT's FormGroup, so on a form where the steps are unkeyed every step's
   * `formControl` is the same object — the wizard's root form. Deriving status from it made all
   * steps share one verdict: typing the KvK number on step 1 flipped steps 2 and 3 to the same
   * badge, because the single shared control was now dirty and invalid.
   *
   * `engaged` (see {@link #visitedSteps}) is what gates the incomplete badge, NOT control state.
   * `dirty` looks like the right signal and is not: several field types write their own value while
   * initialising, and a write through a ControlValueAccessor marks the control dirty with no human
   * involved. On the carrier onboarding that put a dashed "you left this half done" badge on the
   * contacts and documents steps the moment the KvK number was typed on step one.
   *
   * The virtual review step has no fields of its own, so it stays `not-started` — matching the
   * design, where the final "Controleren" step renders as a plain outlined step.
   */
  #stepStatus(step: FormlyFieldConfig | undefined, engaged: boolean): WizardStepStatus {
    if (!step) {
      return WizardStepStatusEnum.NotStarted;
    }
    const leaves = this.#leafControls(step);
    if (!leaves.length) {
      return WizardStepStatusEnum.NotStarted;
    }
    if (leaves.some(control => control.invalid)) {
      return engaged ? WizardStepStatusEnum.Incomplete : WizardStepStatusEnum.NotStarted;
    }
    // `treatFalseAsEmpty`: a step made of checkboxes reports `false` for every field from the moment
    // the form initialises. Counting that as content would hand it a check badge before the user had
    // seen it — the same "credit for progress nobody made" the prefill rule above exists to prevent.
    return leaves.every(control => isEmptyValue(control.value, true))
      ? WizardStepStatusEnum.NotStarted
      : WizardStepStatusEnum.Complete;
  }

  /**
   * The controls of a step's own leaf fields. Only KEYED fields are collected: an unkeyed field
   * shares its parent's control, so including one would drag a sibling step's state — or the whole
   * form's — into this step's verdict, which is the bug this exists to avoid.
   *
   * Falls back to the step's own control when the step has no keyed descendants at all (a step
   * built entirely from display-only fields), but never to the wizard's root form, for the same
   * reason.
   */
  #leafControls(step: FormlyFieldConfig): AbstractControl[] {
    const found: AbstractControl[] = [];
    const walk = (field: FormlyFieldConfig): void => {
      // A hidden field's control is detached from the form (Formly's unregisterControl calls
      // setParent(null)) but the object lives on with its validators and stays INVALID forever.
      // Counting it would pin a step with a conditionally hidden required field on the incomplete
      // badge while the form itself is valid and submit succeeds.
      if (field.hide === true) {
        return;
      }
      if (field.fieldGroup?.length) {
        field.fieldGroup.forEach(walk);
        return;
      }
      if (field.key !== undefined && field.formControl?.parent) {
        found.push(field.formControl);
      }
    };
    (step.fieldGroup ?? []).forEach(walk);
    if (!found.length && step.formControl && step.formControl !== this.field.formControl) {
      found.push(step.formControl);
    }
    return found;
  }

  /**
   * Check if draft feature is enabled (default: true)
   * Can be disabled via props.enableDraft = false
   */
  private get isDraftEnabled(): boolean {
    return this.field.props?.enableDraft !== false;
  }

  ngOnInit(): void {
    this.#moduleId =
      this.options?.formState?.entity?.id || this.options?.formState?.moduleId || this.options?.formState?.entry?.id;
    this.#draftEntryId = (this.options?.formState?.entry as Entry | undefined)?.id;

    if (this.isDraftEnabled && this.#moduleId) {
      // Drafts written before the key became entry-scoped are module-wide and would overwrite any
      // entry's server values — drop them instead of restoring them.
      this.#draftService.clearLegacyDraft(this.#moduleId);
      this.loadDraft();
    }

    // After the draft restore, so a caller that asked for a specific step wins over the position
    // the draft remembered. Outside the draft branch on purpose: loadDraft returns early on several
    // paths (drafts disabled, no module id, no draft), and the start step must survive all of them.
    this.#applyStartStep();

    // Live-track Tailwind sm breakpoint zodat resize correct re-mount triggert.
    this.#breakpointObserver
      .observe('(min-width: 640px)')
      .pipe(takeUntil(this.#destroy$))
      .subscribe(state => this.isDesktop.set(state.matches));

    // Re-render when form values change so conditional hide/show expressions take effect
    if (this.field.formControl) {
      this.field.formControl.valueChanges.pipe(takeUntil(this.#destroy$)).subscribe(() => {
        // Conditional steps may appear/disappear; keep the modal header badge in sync.
        this.#publishStepInfo();
        // Re-evaluate completion-button conditions (visibleCompletionActions computed).
        this.#modelVersion.update(v => v + 1);
        this.#cdr.markForCheck();
      });

      /*
        Touching a control emits no value change, so the stepper badges would go stale whenever
        something OUTSIDE this component touches the form — DetailsService marks the whole entry
        form touched when a page-hosted save is blocked, and this component is an OnPush descendant
        that click never marks dirty. Without this the steps keep reading "niet gestart" at the exact
        moment the user is being told what is missing.
      */
      this.field.formControl.events
        .pipe(
          filter(event => event instanceof TouchedChangeEvent),
          takeUntil(this.#destroy$)
        )
        .subscribe(() => {
          this.#touchedRevision.update(v => v + 1);
          this.#cdr.markForCheck();
        });
    }
  }

  /**
   * Open on the step the host asked for (the carrier clicking a flagged step on the onboarding
   * status page). Read from {@link WizardHostContext.consumeStartTarget} and consumed ONCE — so a
   * later re-open of the same form does not jump again, which would strand a user who navigated
   * away from that step on purpose. On the no-stepper (mobile) path, `goToStep` itself persists the
   * new position into the draft — so even though the target is only read once, a reload still lands
   * on that step because it is now the saved draft position, not because of this input.
   *
   * The host hands over a flag TARGET (`page:<key>`), never an index. It computes it while this
   * component is unmounted, so the `hide` expressions of conditional pages have not been evaluated
   * on its side yet and its page list can differ from {@link visibleSteps} — an index would then
   * point at the wrong step, or past the end. Resolving the target here, against the list the
   * stepper actually walks, is the whole point of the handshake. The key is consumed even when it
   * resolves to nothing, so a target for a page that is hidden right now cannot fire later.
   */
  #applyStartStep(): void {
    const startTarget = this.#hostContext?.consumeStartTarget();
    if (!startTarget) {
      return;
    }
    const startStep = this.visibleSteps.findIndex(step => pageTarget(pageKeyOf(step)) === startTarget);
    if (startStep < 0) {
      return;
    }
    this.goToStep(startStep);
    /*
      The synchronous call above can only move the index: the ViewChild stepper does not exist yet
      during ngOnInit. On desktop the Material stepper is the thing that actually decides which step
      is shown, and the draft restore advances it from a setTimeout queued BEFORE this one — so
      without re-applying here, a draft on a later step would silently win the race.
    */
    this.#startStepTimer = setTimeout(() => {
      this.#startStepTimer = null;
      if (!this.stepper) {
        return;
      }
      /*
        [linear] mat-stepper rejects a bare `selectedIndex` write: CdkStepper's
        `_anyControlsInvalidOrPending` treats any skipped step as blocking unless it is already
        `completed` (or `interacted`). By this point the draft restore has run and `isStepCompleted`
        reports those intermediate steps incomplete again (currentStepIndex hasn't caught up yet),
        so `goToStep`'s assignment would be silently ignored. Mark the skipped steps completed so
        the linear gate lets the jump through — and hand the flags straight back.

        The restore is what keeps this a jump instead of a permanent unlock: `MatStep.completed` is
        an OVERRIDE, and the `[completed]` binding only writes again when its own expression
        changes — which for a step that stays incomplete it never does. Leaving the flags set opened
        the linear gate for the rest of the session, so a header click could walk past a step the
        wizard was refusing to let anyone leave. The setter applies synchronously, so by the time
        the loop below runs the stepper has already read them.
      */
      const skipped = this.stepper.steps.toArray().slice(0, startStep);
      const completedBefore = skipped.map(step => step.completed);
      skipped.forEach(step => (step.completed = true));
      this.stepper.selectedIndex = startStep;
      skipped.forEach((step, index) => (step.completed = completedBefore[index]));
    });
  }

  ngOnDestroy(): void {
    // A wizard torn down before the deferred start-step jump ran (the host leaving edit mode, a
    // conditional page swap) must not have that jump land on the stepper it left behind.
    if (this.#startStepTimer !== null) {
      clearTimeout(this.#startStepTimer);
      this.#startStepTimer = null;
    }
    // Save draft when component is destroyed (e.g., navigation away)
    if (this.isDraftEnabled && this.#moduleId && !this.#draftLoaded) {
      this.saveDraft();
    }
    // Release the projected footer template so the modal footer clears.
    this.#footerSlot?.content.set(null);
    this.#footerSlot?.stepInfo.set(null);
  }

  /**
   * When hosted with a WizardFooterSlot (create-form modal), publish the current-step button
   * template into the modal's full-width footer. Inline button rows are hidden via `hasFooterSlot`.
   */
  ngAfterViewInit(): void {
    this.#footerSlot?.content.set(this.runnerFooterTpl);
    this.#publishStepInfo();
  }

  /**
   * After every change-detection cycle, sync the visual "blocked" state of each
   * mat-step-header with `isStepReachable`. We use a transparent overlay to:
   *   1. show a `not-allowed` cursor over unreachable step headers
   *   2. swallow click events before mat-stepper sees them, eliminating the
   *      navigate→revert flicker that pure `selectionChange` reverting causes
   *
   * Cheap when nothing changed (classList/dataset checks are idempotent).
   */
  ngAfterViewChecked(): void {
    this.#syncBlockedStepHeaders();
  }

  #syncBlockedStepHeaders(): void {
    const headers: NodeListOf<HTMLElement> = this.#elementRef.nativeElement.querySelectorAll('mat-step-header');
    if (!headers || headers.length === 0) {
      return;
    }
    headers.forEach((header, index) => {
      const blocked = !this.isStepReachable(index);
      const alreadyBlocked = header.dataset['wizardBlocked'] === 'true';
      if (blocked && !alreadyBlocked) {
        this.#addBlockedOverlay(header);
        header.dataset['wizardBlocked'] = 'true';
      } else if (!blocked && alreadyBlocked) {
        this.#removeBlockedOverlay(header);
        delete header.dataset['wizardBlocked'];
      }
    });
  }

  #addBlockedOverlay(header: HTMLElement): void {
    if (header.querySelector(':scope > .wizard-blocked-overlay')) {
      return;
    }
    // Make sure absolute children anchor against the header itself.
    if (getComputedStyle(header).position === 'static') {
      header.style.position = 'relative';
    }
    const overlay = document.createElement('div');
    overlay.className = 'wizard-blocked-overlay';
    overlay.style.cssText = 'position:absolute;inset:0;cursor:not-allowed;z-index:10;background:transparent;';
    // Capture-phase listener so we run before mat-step-header's own click handler.
    overlay.addEventListener(
      'click',
      (event: Event) => {
        event.stopPropagation();
        event.preventDefault();
      },
      true
    );
    header.appendChild(overlay);
  }

  #removeBlockedOverlay(header: HTMLElement): void {
    const overlay = header.querySelector(':scope > .wizard-blocked-overlay');
    if (overlay) {
      overlay.remove();
    }
  }

  /**
   * Load draft data if available
   */
  private loadDraft(): void {
    if (!this.#moduleId) return;

    // Editing a persisted entry whose id hasn't reached formState yet: a 'new'-keyed draft belongs
    // to a different (unsaved) entry, so restoring it here would overwrite the loaded server values.
    if (!this.#draftEntryId && this.options?.formState?.isNew === false) {
      return;
    }

    const draft = this.#draftService.getDraft(this.#moduleId, this.#draftEntryId);

    if (draft && draft.data) {
      // Apply draft data to model
      if (this.model && typeof this.model === 'object') {
        Object.assign(this.model, draft.data);

        this.field.formControl.patchValue(draft.data);
      }

      // Restore step position
      this.currentStepIndex = draft.currentStep || 0;

      // Update stepper position after view init
      setTimeout(() => {
        if (this.stepper && draft.currentStep > 0) {
          for (let i = 0; i < draft.currentStep; i++) {
            this.stepper.next();
          }
        }
      });

      this.#draftLoaded = true;
    }
  }

  /**
   * Save current form state as draft
   */
  private saveDraft(): void {
    if (!this.#moduleId || !this.isDraftEnabled) return;

    const formValue = this.form?.getRawValue() || {};
    this.#draftService.saveDraft(this.#moduleId, formValue, this.currentStepIndex, this.#draftEntryId);
  }

  // Handle form submission
  getCurrentStep() {
    return this.visibleSteps[this.currentStepIndex];
  }

  isLastStep(): boolean {
    return this.currentStepIndex === this.totalStepCount - 1;
  }

  /**
   * Check if the "next" button should be hidden for a step.
   * Uses the step's nextButtonConditions if defined.
   */
  isNextButtonHidden(step: FormlyFieldConfig): boolean {
    return this.#formService.nextButtonHiddenConditionalFunction()(step);
  }

  /**
   * A step is visually completed only after the user has progressed beyond it.
   * Formly can initialise future step controls as valid, but that is not evidence
   * of user progress and must not render Material's editable pencil icon.
   */
  isStepCompleted(stepIndex: number, step: FormlyFieldConfig): boolean {
    return stepIndex < this.currentStepIndex && step.formControl?.valid === true && !this.isNextButtonHidden(step);
  }

  /**
   * Whether a step (by visible-steps index) is reachable via stepper navigation.
   * A step is unreachable when any preceding step has its "next" button hidden —
   * because in that case the user has no way to legitimately progress past that
   * earlier step.
   *
   * Primary blocking happens via mat-step `[completed]` (a step is not "completed"
   * when its Next button is hidden, so linear-mode stepper rejects forward header
   * clicks). This helper is used as a safety net in `onStepChange` to revert any
   * forward navigation that slips through.
   */
  isStepReachable(stepIndex: number): boolean {
    // Non-linear: every step is reachable by definition — the user fills them in any order and the
    // whole form is validated once on the review step.
    if (this.isNonLinear()) {
      return true;
    }
    if (stepIndex === 0) {
      return true;
    }
    const steps = this.visibleSteps;
    for (let i = 0; i < stepIndex; i++) {
      if (this.isNextButtonHidden(steps[i])) {
        return false;
      }
    }
    // Steps at or behind the current one are always reachable: the user has been there, and a linear
    // mat-stepper lets them walk back freely.
    if (stepIndex <= this.currentStepIndex) {
      return true;
    }
    /*
      Forward, mirror CdkStepper._anyControlsInvalidOrPending: every step from the current one up to
      the target must be valid and not pending, and every step past the current one must have been
      interacted with (left at least once — the current step is marked interacted by the jump
      itself). `[completed]` is false for all of them, so it never overrides. A fresh wizard can
      therefore only reach the next step; after walking back, the steps already done stay open.
      Saying "reachable" anywhere else drew a clickable-looking step that did nothing on click.
    */
    for (let i = this.currentStepIndex; i < stepIndex; i++) {
      const status = steps[i]?.formControl?.status;
      if (status === 'INVALID' || status === 'PENDING') {
        return false;
      }
      if (i > this.currentStepIndex && !this.#stepInteracted(i)) {
        return false;
      }
    }
    return true;
  }

  /**
   * CdkStepper's own `interacted` flag where the MatStepper exists; on mobile (no MatStepper) the
   * visit record stands in for it.
   */
  #stepInteracted(index: number): boolean {
    return this.stepper?.steps?.get(index)?.interacted ?? this.#visitedSteps.has(this.#stepIdentity(index));
  }

  /**
   * Whether a step carries a configured interface action on its "Volgende stap" button.
   * Such steps run+await the action (creating an entry) before advancing.
   */
  stepHasAction(step: FormlyFieldConfig): boolean {
    return !!step?.props?.['interfaceActionId'];
  }

  /**
   * Runner context = the wizard is hosted by the Create-Form runner modal, which supplies an
   * `onWizardComplete` callback in formState. Only then do we render the in-wizard "Voltooien"
   * button; normal module forms are submitted by the surrounding form host (DetailsService).
   */
  isRunnerContext(): boolean {
    return typeof this.options?.formState?.onWizardComplete === 'function';
  }

  /**
   * DetailsService of the hosting form (supplied via formState) — used to surface an action-create
   * failure as a persistent alert above the form, next to the snackbar.
   */
  get #hostDetailsService():
    | { reportRunnerError: (error: unknown) => void; clearRunnerError: () => void }
    | undefined {
    const host = this.options?.formState?.detailsService;
    return typeof host?.reportRunnerError === 'function' ? host : undefined;
  }

  /** Clear a previous action-create failure before a new attempt. */
  #clearActionError(): void {
    this.#hostDetailsService?.clearRunnerError();
  }

  /**
   * Report a failed action-create with the backend's validation messages (e.g. a duplicate e-mail)
   * instead of a bare "probeer opnieuw". Falls back to a snackbar-only report without a host form.
   */
  #reportActionError(error: unknown): void {
    const host = this.#hostDetailsService;
    if (host) {
      host.reportRunnerError(error);
      return;
    }
    this.#utilityService.notify(UtilityService.saveErrorMessage(error));
  }

  /** Per-form configured completion buttons on the last step; empty for plain wizards. */
  get completionActions(): CompletionAction[] {
    return (this.field.props?.['completionActions'] as CompletionAction[] | undefined) ?? [];
  }

  /**
   * Completion buttons whose optional condition matches the current form model AND whose linked
   * interface action is allowed for the current user's roles. Recomputes on every form-value change
   * (via #modelVersion) so switching the branch radio shows/hides the right buttons, and on a
   * user-role change (the role gate reads UserStore through the service).
   *
   * The role gate matters most on page-hosted onboarding wizards: a button like "Stuur naar
   * gebruiker" is only meant for the employee filling the form in on someone else's behalf, and is
   * hidden from the onboarding user themselves by restricting its action's userRoles.
   */
  readonly visibleCompletionActions = computed<CompletionAction[]>(() => {
    this.#modelVersion();
    return this.completionActions.filter(ca => this.#matchesCondition(ca) && this.#isAllowedForCurrentUser(ca));
  });

  /**
   * True when the current user's roles allow the completion action's linked interface action.
   * Fails open when the action cannot be resolved — an unknown interfaceActionId is a configuration
   * problem, and the run itself is still validated server-side.
   */
  #isAllowedForCurrentUser(ca: CompletionAction): boolean {
    const action = this.#resolveCompletionActionModule(ca)?.interfaceActions?.find(
      a => a.id === ca.interfaceActionId
    );
    return this.#interfaceActionsService.isAllowedForCurrentUser(action);
  }

  /** Visible completion buttons in Figma order: secondary (reset) left, primary (close/navigate) right. */
  readonly sortedVisibleCompletionActions = computed<CompletionAction[]>(() =>
    [...this.visibleCompletionActions()].sort(
      (a, b) => (a.behavior === 'reset' ? 0 : 1) - (b.behavior === 'reset' ? 0 : 1)
    )
  );

  /**
   * Page-hosted (non-runner) completion buttons in Figma order: secondary (outline) left, primary
   * (filled) right. Ordering keyed on buttonStyle rather than behavior, since the page flow uses
   * behavior for post-action routing, not appearance.
   */
  readonly sortedPageCompletionActions = computed<CompletionAction[]>(() =>
    [...this.visibleCompletionActions()].sort(
      (a, b) => (a.buttonStyle === 'outline' ? 0 : 1) - (b.buttonStyle === 'outline' ? 0 : 1)
    )
  );

  /**
   * True when the button has no condition, or when the form-model value at its condition keyPath
   * equals the configured value. The keyPath is `entry.`-prefixed and resolved against
   * `{ entry: model }`, exactly like conditional-field rules elsewhere (see FormService).
   */
  #matchesCondition(ca: CompletionAction): boolean {
    const condition = ca.condition;
    if (!condition?.fieldKeyPath) {
      return true;
    }
    const model = this.form?.getRawValue() ?? {};
    const actual = UtilityService.keyPathToValue({ entry: model, id: undefined } as Entry, condition.fieldKeyPath);
    if (actual !== null && typeof actual === 'object') {
      const obj = actual as Record<string, unknown>;
      return obj['value'] === condition.value || obj['id'] === condition.value || obj['slug'] === condition.value;
    }
    return actual === condition.value;
  }

  /**
   * Resolve the module that owns the step's interface actions. Mirrors
   * InterfaceActionButtonFormlyComponent.#getActionModule: prefer interfaceActionModuleCode,
   * fall back to formState.entity.
   */
  #getStepActionModule(step: FormlyFieldConfig): Module | null {
    const moduleCode = step?.props?.['interfaceActionModuleCode'] as string | undefined;
    if (moduleCode) {
      // A wizard-step action may live on a normal module OR on the create-form itself (type:'form').
      const found =
        this.#modulesStore.enabledModules().find(m => m.code === moduleCode) ??
        this.#modulesStore.enabledForms().find(m => m.code === moduleCode);
      if (found) {
        return found;
      }
    }
    return (this.options?.formState?.entity as Module) ?? null;
  }

  /** Resolve the configured InterfaceAction for a step (by id, within the step's action module). */
  #getStepAction(step: FormlyFieldConfig): InterfaceAction | undefined {
    const actionId = step?.props?.['interfaceActionId'] as string | undefined;
    const module = this.#getStepActionModule(step);
    return module?.interfaceActions?.find(a => a.id === actionId);
  }

  /** Resolve a configured InterfaceAction by id (in an explicit module or formState.entity). */
  #findAction(actionId: string, moduleCode?: string): InterfaceAction | undefined {
    const module = moduleCode
      ? (this.#modulesStore.enabledModules().find(m => m.code === moduleCode) ??
         this.#modulesStore.enabledForms().find(m => m.code === moduleCode))
      : (this.options?.formState?.entity as Module | undefined);
    return module?.interfaceActions?.find(a => a.id === actionId);
  }

  /**
   * Run a configured completion action: create the target entry via the runner (awaited), then
   * either close (behavior 'close') or persist + reset for another entry (behavior 'reset').
   */
  async runCompletionAction(ca: CompletionAction): Promise<void> {
    if (this.isAdvancing()) {
      return;
    }
    // Non-linear surfaces what is missing instead of silently doing nothing on an invalid form.
    if (this.isNonLinear()) {
      if (!this.#allowsIncompleteSubmit(ca) && !this.#validateBeforeSubmit()) {
        return;
      }
    } else if (!this.form.valid) {
      return;
    }
    const action = this.#findAction(ca.interfaceActionId, ca.interfaceActionModuleCode);
    const actionStep = action?.actions?.[0];
    if (!action || !actionStep || !this.#interfaceActionsService.isVisibleInContext(action, 'formButton')) {
      this.#utilityService.notify('De gekoppelde actie is niet beschikbaar.');
      return;
    }
    this.isAdvancing.set(true);
    this.#clearActionError();
    this.#cdr.markForCheck();
    const seed: Entry = { entry: { ...(this.form?.getRawValue() ?? {}), archived: false } };
    try {
      // In the Create-Form runner, replace/update persistence routes the completion through the
      // modal's create-or-patch path (patch the existing record) instead of creating a duplicate;
      // outside that context (persistTargetEntry undefined) it creates as before.
      const persist = this.options?.formState?.persistTargetEntry;
      const created = (await firstValueFrom(
        persist
          ? persist(actionStep, seed, this.#stepResults)
          : this.#runner.runActionCreate(actionStep, seed, this.#stepResults)
      )) as Entry;
      this.#stepResults[ca.interfaceActionId] = created;
      const model = this.form.getRawValue();
      const complete = this.options?.formState?.onWizardComplete;
      if (ca.behavior === 'reset') {
        complete?.({ ...this.#stepResults }, model, 'addAnother');
        this.clearForm();
      } else if (ca.behavior === 'navigate') {
        // Persist + close, then let the host modal navigate to the target route with the created
        // entry id as the `userId` query param (id lives at Entry.id — entryIdKeyPath 'id').
        complete?.({ ...this.#stepResults }, model, 'navigate', { navigateTo: ca.navigateTo, entryId: created?.id });
      } else {
        complete?.({ ...this.#stepResults }, model, 'close');
      }
    } catch (error) {
      debugLog('Wizard', 'completion action failed', error);
      this.#reportActionError(error);
    } finally {
      this.isAdvancing.set(false);
      this.#cdr.markForCheck();
    }
  }

  /**
   * Run a completion action on a PAGE-hosted wizard (no create-form runner). Mirrors the
   * interface-action-button flow: run the host's async validation, then fire the linked interface
   * action through the shared handleCustomAction path. The action is an editValue targeting the
   * current entry, so it persists the form values it carries (via formState.entry, which merges the
   * live model) AND the action's mapped changes in one save — no separate save call, matching the
   * button field. On a 'navigate' completion it routes to navigateTo afterwards; 'close'/'reset'
   * stay on the page (the runner-only reset/close semantics do not apply here).
   *
   * Kept separate from runCompletionAction (the runner create-and-close path) so neither flow can
   * regress the other.
   */
  runPageCompletionAction(ca: CompletionAction): void {
    if (this.isAdvancing() || this.submitDisabled()) {
      return;
    }

    // Non-linear: the button was clickable regardless of form state. The SECONDARY button
    // (buttonStyle 'outline', e.g. "Naar gebruiker sturen") is deliberately allowed on an
    // incomplete form — handing the rest over to the user is the whole point. The primary button
    // must pass full validation first. Keyed on buttonStyle, not behavior: the primary action here
    // is a 'navigate' one too.
    if (this.isNonLinear() && !this.#allowsIncompleteSubmit(ca) && !this.#validateBeforeSubmit()) {
      return;
    }

    const module = this.#resolveCompletionActionModule(ca);
    const action = module?.interfaceActions?.find(a => a.id === ca.interfaceActionId);
    const entry = this.#completionActionEntry();

    if (!action || !module || !entry || !this.#interfaceActionsService.isVisibleInContext(action, 'formButton')) {
      this.#utilityService.notify('De gekoppelde actie is niet beschikbaar.');
      return;
    }

    this.isAdvancing.set(true);
    this.#clearActionError();
    this.#cdr.markForCheck();

    // Mirror InterfaceActionButtonFormlyComponent: run async validation (no-op without async
    // validators), then fire the interface action.
    const detailsService = this.options?.formState?.detailsService as
      | { validateForm?: () => Observable<boolean> }
      | undefined;
    const asyncValidation$ = detailsService?.validateForm ? detailsService.validateForm() : of(true);

    asyncValidation$.pipe(take(1)).subscribe({
      next: isValid => {
        if (!isValid) {
          this.isAdvancing.set(false);
          this.#cdr.markForCheck();
          return;
        }
        void this.#executePageCompletionAction(ca, action, module, entry);
      },
      error: error => {
        debugLog('Wizard', 'page completion validation failed', error);
        this.#reportActionError(error);
        this.isAdvancing.set(false);
        this.#cdr.markForCheck();
      },
    });
  }

  /**
   * The entry a page completion action operates on: the live form model, but carrying the PERSISTED
   * identity (id/_etag).
   *
   * On a page that resolves its entry by query param — the onboarding opened with `?userId=` — the
   * entry never lands in the ModulesStore cache DetailsService reads from, so `formState.entry`
   * holds the model without an id. An editValue step then saw `id == null`, created a fresh entry
   * instead of editing the real one and (before the navigateBack fix) sent the user a step back
   * (#20719). `formState.hostEntry` is the persisted record — {@link anchorEntryOf} is the shared
   * `entry` vs `hostEntry` discriminator (also used by the documents checklist), so this stays in
   * sync with it instead of re-deriving the same rule.
   *
   * The LIVE form value is laid over whichever of the two wins. `formState.entry` is a host
   * snapshot that only refreshes through `DetailsService.formValue`, which does not observe this
   * wizard's form during an edit session — so an edit-after-submit sent the persisted values
   * straight back and silently dropped what the user had just typed. The form is the same source
   * {@link #conditionMatches} already trusts for the live model.
   */
  #completionActionEntry(): Entry | undefined {
    const base = anchorEntryOf(this.options?.formState);
    if (!base) {
      return undefined;
    }
    // `formState.entry` is read again here (not only through anchorEntryOf above) because it can
    // carry keys with no backing form control — those must survive even on the branch where
    // anchorEntryOf resolves `base` to `hostEntry` instead. Three layers, later wins: the
    // persisted/host base, then formState.entry's own sub-bag, then the live form snapshot.
    const stateEntry = this.options?.formState?.entry as Entry | undefined;
    return {
      ...base,
      entry: { ...base.entry, ...(stateEntry?.entry ?? {}), ...(this.form?.getRawValue() ?? {}) },
    };
  }

  /**
   * Run a page completion action and only navigate once it has actually been saved.
   *
   * A 'navigate' completion routes the visitor away from the form (onboarding → status page), so
   * the save must be awaited: `handleCustomAction` stages its work via setTimeout and reports how
   * each step settled through the `ActionStepOutcome[]` it returns. Action steps the awaitable
   * runner covers (editValue/create without user input) therefore run through it directly, awaited
   * step by step, in the branch below.
   *
   * Everything else — user-input popups, impersonate, navigate steps — still dispatches through
   * `handleCustomAction`'s shared staging, but this method subscribes to its returned outcomes
   * instead of notifying/navigating the instant the steps are staged: doing that unconditionally
   * made a still-pending (or ultimately failed) save look submitted (#21303). A completion that
   * stays on the page follows this same branch too — only a 'navigate' completion can ever be
   * `awaitable` below — where the store-backed save is what keeps the surrounding view in sync.
   */
  async #executePageCompletionAction(
    ca: CompletionAction,
    action: InterfaceAction,
    module: Module,
    entry: Entry
  ): Promise<void> {
    const steps = action.actions ?? [];
    const awaitable =
      ca.behavior === 'navigate' &&
      steps.length > 0 &&
      steps.every(
        step =>
          (step.type === 'editValue' || step.type === 'create') &&
          !step.entryValues?.some(value => value.basedOn === 'userInput')
      );

    if (!awaitable) {
      if (steps.length === 0) {
        // Opus-verified 2026-09-14: a zero-step completion action (e.g. a 'navigate'
        // completion with no configured steps) is instantly complete. `executeActions` resolves
        // with an empty outcomes array for this case too (interface-actions.service.ts ~1013
        // `if (!actions.length) return of([])`) — identical to what a CANCELLED popup produces —
        // so the two cannot be told apart once dispatched. Deciding here, before dispatch, avoids
        // misreading a zero-step completion as a cancel below (main's synchronous navigate never
        // had this ambiguity: it never subscribed to an outcomes array in the first place).
        this.#notifyPageCompletion(ca);
        if (ca.behavior === 'navigate') {
          this.#navigateAfterCompletion(ca);
        }
        this.isAdvancing.set(false);
        this.#cdr.markForCheck();
        return;
      }

      // Opus-verified 2026-09-14: `handleCustomAction`'s outcomes can settle well after
      // this call returns — each step is staggered by `setTimeout(…, 2500 * index)` on top of its
      // own HTTP round trip, bounded by `STEP_SETTLE_TIMEOUT_MS` per step (see that constant's
      // doc in interface-actions.service.ts).
      //
      // Opus-verified — regression fix: this used to ALSO start a local `setTimeout`, sized
      // to `STEP_SETTLE_TIMEOUT_MS`, purely as a backstop for `handleCustomAction` throwing
      // synchronously before the Observable was even subscribed to. That backstop was both
      // redundant and actively harmful, so it was removed instead of kept:
      // - Redundant: a SYNCHRONOUS throw is caught by the `catch` block below in the same tick —
      //   long before any timer, however short, could ever fire. `finalize` (context7
      //   /reactivex/rxjs, finalize.ts: the callback runs exactly once, on complete, error, OR
      //   unsubscribe) already releases the lock on every path the Observable itself can take,
      //   `takeUntil(this.#destroy$)` on destroy included.
      // - Harmful: the timer started at DISPATCH — before `handleCustomAction` even runs — while a
      //   `handleCustomAction` call whose action has `userInput` values first shows a confirmation
      //   popup and only calls `executeActions` (and starts ITS OWN, honest 120s-per-step clock)
      //   once that popup is confirmed (`interface-actions.service.ts#handleCustomAction`,
      //   `switchMap(popupValues => ...)`). A popup left open for longer than
      //   `STEP_SETTLE_TIMEOUT_MS` — entirely plausible, it is user-paced — made this local timer
      //   fire FIRST, re-enabling the Next/Voltooien button (and every other action sharing
      //   `isAdvancing`) while the real save had not even been dispatched yet.
      try {
        // navigateBack false: this button belongs to the page, so a step that ends up creating an
        // entry must not send the visitor one step back in the history (#20719 'Naar gebruiker
        // sturen' landed back on the user page).
        this.#interfaceActionsService
          .handleCustomAction({ id: action.id, entry, interfaceAction: action }, module, true, false)
          .pipe(
            takeUntil(this.#destroy$),
            finalize(() => {
              this.isAdvancing.set(false);
              this.#cdr.markForCheck();
            })
          )
          .subscribe({
            next: outcomes => {
              if (outcomes.length === 0) {
                // Popup cancelled, or the action was not visible for this entry — nothing was
                // dispatched (steps.length > 0 is guaranteed above, so this can no longer be a
                // zero-step action), so there is nothing to notify or navigate for.
                return;
              }
              const failedStep = outcomes.find(outcome => outcome.status === 'failed');
              if (failedStep) {
                this.#reportActionError(failedStep.error ?? new Error('Interface action step failed'));
                return;
              }
              // Opus-verified: 'unsettled' means the outcome is UNKNOWN, not a confirmed
              // failure (e.g. an `editValue` step still blocked on the store's user-paced
              // 412-conflict dialog past `STEP_SETTLE_TIMEOUT_MS`). Treat it as not-succeeded —
              // no notify/navigate, since the save may not actually be done — without claiming it
              // definitely failed.
              const unsettledStep = outcomes.find(outcome => outcome.status === 'unsettled');
              if (unsettledStep) {
                this.#reportActionError(unsettledStep.error ?? new Error('Interface action step did not confirm completion in time'));
                return;
              }
              this.#notifyPageCompletion(ca);
              if (ca.behavior === 'navigate') {
                this.#navigateAfterCompletion(ca);
              }
            },
            error: error => {
              debugLog('Wizard', 'page completion action failed', error);
              this.#reportActionError(error);
            },
          });
      } catch (error) {
        // handleCustomAction threw before returning an Observable — finalize above never ran.
        debugLog('Wizard', 'page completion action failed', error);
        this.#reportActionError(error);
        this.isAdvancing.set(false);
        this.#cdr.markForCheck();
      }
      return;
    }

    try {
      // Sequential: a later step may consume an earlier one via basedOn:'stepResult'.
      for (const step of steps) {
        await firstValueFrom(
          step.type === 'create'
            ? this.#runner.runActionCreate(step, entry, this.#stepResults)
            : this.#runner.runActionEdit(step, entry, this.#stepResults)
        );
      }
      this.#notifyPageCompletion(ca);
      this.#navigateAfterCompletion(ca);
    } catch (error) {
      debugLog('Wizard', 'page completion action failed', error);
      this.#reportActionError(error);
    } finally {
      this.isAdvancing.set(false);
      this.#cdr.markForCheck();
    }
  }

  /**
   * Resolve the module owning a completion action: an explicit interfaceActionModuleCode (normal
   * module or create-form), else the form's own entity (formState.entity).
   */
  #resolveCompletionActionModule(ca: CompletionAction): Module | undefined {
    if (ca.interfaceActionModuleCode) {
      return (
        this.#modulesStore.enabledModules().find(m => m.code === ca.interfaceActionModuleCode) ??
        this.#modulesStore.enabledForms().find(m => m.code === ca.interfaceActionModuleCode)
      );
    }
    return (this.options?.formState?.entity as Module | undefined) ?? undefined;
  }

  /**
   * Tell the hosting page that a page completion action has run.
   *
   * A page-hosted wizard submits through an interface action, not through the page's own save
   * button, so the page never sees the submit itself. Watching the entry status instead only works
   * while the status actually moves: a carrier resubmitting an 'in-behandeling' onboarding writes
   * that same status back, and the onboarding status page then kept showing the wizard.
   *
   * Deliberately NOT `onWizardComplete`: that key is what marks the runner context
   * (`isRunnerContext`), so publishing it from a page would swap the page's completion buttons for
   * the runner's "Voltooien" button.
   */
  #notifyPageCompletion(ca: CompletionAction): void {
    const notify = this.#hostContext?.onPageComplete;
    if (typeof notify !== 'function') {
      return;
    }
    try {
      notify(ca);
    } catch (error) {
      debugLog('Wizard', 'page completion host callback failed', error);
    }
  }

  /**
   * The carrier opened this wizard from the status page to rework a denied request
   * ({@link WizardHostContext.editSession}) and has changed nothing yet. Resubmitting then would
   * send the reviewer the exact same request back, so the last step offers a way OUT instead of a
   * way to submit (#21303).
   *
   * "Changed" is the HOST's verdict ({@link WizardHostContext.hasUnsavedChanges}), because the
   * host is the only side that can answer the question properly: `dirty` is one-way (typing a
   * character and deleting it again leaves the form dirty for good) and form-local (a document the
   * carrier replaced on the status page never touches this form at all).
   *
   * Opus-verified, doc fix: there is no fallback here for the no-host-context case — without
   * a `WizardHostContext` (a wizard used in a normal module form or entry page) `#hostContext` is
   * `null`, `editSession()` is never true, and this method returns `false` before it ever reaches
   * `hasUnsavedChanges`. The button is simply never offered.
   *
   * A first submission is never gated: without an edit session there is no earlier version to be
   * identical to. So is the runner context, which has no status page to go back to.
   */
  showExitToStatus(): boolean {
    if (this.isRunnerContext()) {
      return false;
    }
    const context = this.#hostContext;
    if (!context?.editSession() || typeof context.onExit !== 'function') {
      return false;
    }
    // `#touchedRevision` is read so an OnPush host still re-renders this button away on the first
    // touch-only edit (no value change). `context.hasUnsavedChanges` is a PULL callback,
    // not a signal: it is re-evaluated fresh on every call, which is what makes it also cover a
    // field written with `{ emitEvent: false }` (autocomplete free-text commit, address,
    // geolocation, inline-entries — see the dozen-field-types note near `#stepRevision` above).
    // `untracked`: the callback reads the HOST's own signals (e.g. `DetailsService.form()`), which
    // must not become a tracked dependency of THIS component's template consumer — this method is
    // called directly from the template, so re-rendering it is `#touchedRevision`'s job, not a
    // side effect of reading someone else's state.
    this.#touchedRevision();
    return !untracked(() => context.hasUnsavedChanges?.() ?? false);
  }

  /**
   * Leave the edit session without running any completion action — the page swaps back to the
   * status view. Deliberately not a router navigation: the wizard does not know where it is hosted,
   * and the host already owns that transition (`exitEditMode`).
   */
  exitToStatus(): void {
    const exit = this.#hostContext?.onExit;
    if (typeof exit !== 'function') {
      return;
    }
    try {
      exit();
    } catch (error) {
      debugLog('Wizard', 'exit-to-status host callback failed', error);
    }
  }

  /**
   * Navigate after a 'navigate' page completion. A ':userId' placeholder in navigateTo is replaced
   * by the current route's `userId` query param, falling back to the current entry id.
   */
  #navigateAfterCompletion(ca: CompletionAction): void {
    if (!ca.navigateTo) {
      return;
    }
    const userId =
      this.#activatedRoute.snapshot.queryParamMap.get('userId') ??
      (this.options?.formState?.entry as Entry | undefined)?.id ??
      '';
    void this.#router.navigateByUrl(ca.navigateTo.replace(':userId', userId));
  }

  /**
   * Advance to the next step. For action-carrying steps this runs+awaits the configured interface
   * action (creating an entry in its target module), stores the created entry under the step's
   * resultKey/key for later steps (basedOn:'stepResult'), and only then advances. On failure it
   * stays on the step. Non-action steps advance synchronously, exactly as before.
   */
  async nextStep(): Promise<void> {
    const step = this.getCurrentStep();
    if (this.isLastStep() || this.isAdvancing()) {
      return;
    }

    // Non-linear mode deliberately keeps "Volgende stap" enabled on an incomplete step. An
    // action-carrying step cannot run its action on invalid data, so advance WITHOUT running it —
    // #runPendingStepActions catches up at submit time, in step order. Blocking here instead would
    // leave the user with a button that looks enabled and does nothing.
    if (step?.formControl?.valid !== true) {
      if (this.isNonLinear()) {
        this.#advance();
      }
      return;
    }

    if (this.stepHasAction(step)) {
      await this.#runStepAction(step);
      return;
    }

    this.#advance();
  }

  /** Run+await an action-carrying step's interface action, then advance on success. */
  async #runStepAction(step: FormlyFieldConfig): Promise<void> {
    if (await this.#executeStepAction(step)) {
      this.#advance();
    }
  }

  /**
   * Run every action-carrying step that has not produced a result yet, in step order (later steps
   * can consume an earlier one via basedOn:'stepResult'). Steps whose action already ran are
   * skipped by #executeStepAction's own idempotency guard, so in linear mode this only leaves the
   * final step — exactly what submit() did before.
   *
   * @returns false as soon as one action fails; #executeStepAction has notified the user by then.
   */
  async #runPendingStepActions(): Promise<boolean> {
    for (const step of this.visibleSteps) {
      if (this.stepHasAction(step) && !(await this.#executeStepAction(step))) {
        return false;
      }
    }
    return true;
  }

  /**
   * Resolve, validate and run a step's configured interface action (creating an entry in its
   * target module) and store the created entry under the step's own key for later steps
   * (basedOn:'stepResult'). Notifies + returns false on a misconfigured/unavailable action or a
   * failed save; returns true on a successful create. Shared by #runStepAction and submit().
   */
  async #executeStepAction(step: FormlyFieldConfig): Promise<boolean> {
    // Idempotency: a successful create stores the created entry under the step's key. If it's
    // already present, skip a second create — this prevents duplicate TARGET-module entries on a
    // "Voltooien" retry (failed submission persist), a double-click during the persist-in-flight
    // window, or a back/forward revisit of an action-carrying step.
    const resultKey = step.key?.toString();
    if (resultKey && this.#stepResults[resultKey] !== undefined) {
      return true;
    }

    const action = this.#getStepAction(step);
    const module = this.#getStepActionModule(step);

    // Anti-misuse: only run when the linked action is actually configured for the formButton context.
    if (!action || !module || !this.#interfaceActionsService.isVisibleInContext(action, 'formButton')) {
      this.#utilityService.notify('De gekoppelde actie is niet beschikbaar.');
      return false;
    }

    const actionStep = action.actions?.[0];
    if (!actionStep) {
      this.#utilityService.notify('De gekoppelde actie heeft geen uit te voeren stap.');
      return false;
    }

    this.isAdvancing.set(true);
    this.#clearActionError();
    this.#cdr.markForCheck();

    // Runner-prefill (formState.entry, bv. user/company/location of template-source van een
    // slotkaart) heeft geen eigen form-control en valt buiten getRawValue(); merge hem eronder
    // zodat basedOn:'dynamic' mappings er wél bij kunnen. Form-waarden winnen bij key-overlap.
    // UITSLUITEND in runner-context: in een edit-form is formState.entry de volledige persisted
    // entry en zou de merge die stilletjes in de aangemaakte sub-entity schrijven (PR-9371-review).
    const prefill = this.isRunnerContext()
      ? ((this.options?.formState?.entry as Entry | undefined)?.entry ?? {})
      : {};
    const seed: Entry = { entry: { ...prefill, ...(this.form?.getRawValue() ?? {}), archived: false } };

    try {
      const created = await firstValueFrom(this.#runner.runActionCreate(actionStep, seed, this.#stepResults));
      // Key by the wizard step's OWN key — the only value a basedOn:'stepResult' consumer can reference.
      if (resultKey) {
        this.#stepResults[resultKey] = created;
      }
      return true;
    } catch (error) {
      debugLog('Wizard', 'step action failed', error);
      this.#reportActionError(error);
      return false;
    } finally {
      this.isAdvancing.set(false);
      this.#cdr.markForCheck();
    }
  }

  /** Move the stepper one step forward and persist the draft. */
  #advance(): void {
    // On desktop the stepper drives the index via onStepChange; only the mobile (no-stepper) path
    // tracks currentStepIndex manually — avoids a double-increment / index desync on desktop.
    if (this.stepper) {
      this.stepper.next();
    } else {
      this.currentStepIndex++;
      // No MatStepper on mobile, so onStepChange never fires and nothing would record the visit or
      // rebuild the review summary.
      this.#bumpStepRevision();
      this.#publishStepInfo();
    }
    // Save draft after step change
    this.saveDraft();
  }

  previousStep(): void {
    if (this.currentStepIndex > 0) {
      this.currentStepIndex--;
      if (this.stepper) {
        this.stepper.previous();
      } else {
        this.#bumpStepRevision();
      }
      this.#publishStepInfo();
      // Save draft after step change
      this.saveDraft();
    }
  }

  onStepChange(event: StepperSelectionEvent): void {
    this.currentStepIndex = event.selectedIndex;
    this.#bumpStepRevision();
    this.#publishStepInfo();
    // Save draft after step change
    this.saveDraft();
  }

  async submit(): Promise<void> {
    if (this.isAdvancing()) {
      return;
    }
    if (this.isNonLinear()) {
      if (!this.#validateBeforeSubmit()) {
        return;
      }
    } else if (!this.form.valid) {
      return;
    }

    // Runner context: the last step may itself carry an interface action (final create), and in
    // non-linear mode earlier action-steps may have been passed without running theirs. Run+await
    // whatever is still outstanding before reporting completion. On a misconfigured/unavailable
    // action or a failed save, #executeStepAction notifies the user; stay open instead of falsely
    // reporting success.
    if (!(await this.#runPendingStepActions())) {
      return;
    }

    // Mark draft as saved so it won't be loaded again
    if (this.#moduleId && this.isDraftEnabled) {
      this.#draftService.markAsSaved(this.#moduleId, this.#draftEntryId);
    }

    // Hand the accumulated created entries AND the raw form model back to the runner modal so it
    // can persist the submission (as an entry on the form-module) and close.
    this.options?.formState?.onWizardComplete?.({ ...this.#stepResults }, this.form.getRawValue(), 'close');
  }

  /**
   * The branch-radio control: a root-level `choice`, or `choice` nested one level deep inside a
   * step group (steps carry keys, so a step-1 radio lives at e.g. `start.choice`).
   */
  #findChoiceControl(): AbstractControl | null {
    const root = this.form.get('choice');
    if (root) {
      return root;
    }
    for (const key of Object.keys(this.form.controls ?? {})) {
      const nested = this.form.get([key, 'choice']);
      if (nested) {
        return nested;
      }
    }
    return null;
  }

  /**
   * Reset the form for "Extra toevoegen" (add another): clear all values and step results, but
   * keep the chosen branch (`choice` control, root or step-nested) and land back on the branch
   * step so the user can immediately fill in the next entry of the same type.
   */
  clearForm(): void {
    const choiceControl = this.#findChoiceControl();
    const choice = choiceControl?.value;
    this.form.reset();
    this.#stepResults = {};
    // "Extra toevoegen" starts a blank entry. Without this the next one opens with every required
    // step already wearing the incomplete badge, inherited from the entry that was just submitted.
    this.#visitedSteps.clear();
    this.#submitBlocked = false;
    this.reviewConfirmed.set(false);
    // Reset the stepper BEFORE restoring `choice`: MatStepper.reset() calls stepControl.reset()
    // on every step, which would otherwise clobber a choice restored earlier. Restore after.
    if (this.stepper) {
      this.stepper.reset();
    }
    if (choice !== undefined && choice !== null) {
      choiceControl?.setValue(choice);
    }
    const branchStepIndex = Math.min(1, this.visibleSteps.length - 1);
    if (this.stepper) {
      this.stepper.selectedIndex = branchStepIndex;
    }
    this.currentStepIndex = branchStepIndex;
    this.#publishStepInfo();
    if (this.#moduleId && this.isDraftEnabled) {
      this.#draftService.markAsSaved(this.#moduleId, this.#draftEntryId);
    }
    this.#cdr.markForCheck();
  }
}
